// ════════════════════════════════════════════════════════════════════════
// Traducteur de secours NL — le français écrit EN DUR, hors tx().
//
// Fondateur, 23/10/2026 : « fais le chantier du français en dur restant ».
//
// POURQUOI UN FILET CENTRAL. tx('…') ne traduit que ce qu'un développeur a pensé
// à envelopper. Mesuré le 23/10/2026 sur les écrans qu'un compte client peut
// ouvrir : 2 869 textes français distincts écrits en dur (JSX, placeholder,
// libellés de listes, messages) sans passer par tx(). Les envelopper un par un
// dans 160 fichiers, c'est le chantier qui ne finit jamais — un texte neuf
// écrit demain en dur reste en français.
//
// CE QUE FAIT LE MODULE, quand l'app est en néerlandais :
//   · il lit chaque nœud de texte et les attributs placeholder / title /
//     aria-label / alt ; si le texte entier (espaces repliés) est une clé du
//     dictionnaire COMMON (ou de l'overlay auto), il le remplace par sa
//     traduction NL ;
//   · il observe le DOM (ajouts, changements de texte) et traduit au fil de l'eau ;
//   · il se REND : au retour en français, chaque texte retrouve son original.
//
// CE QU'IL NE FAIT PAS, et c'est voulu :
//   · il ne traduit QUE l'égalité exacte d'un texte entier — jamais de
//     traduction mot à mot, jamais de devinette ;
//   · il ne modifie pas la STRUCTURE du DOM (il change `nodeValue` en place), donc
//     React garde ses références : contrairement à un traducteur de page, il ne
//     casse pas la réconciliation ;
//   · il laisse tels quels les zones où le français est voulu : `[lang="fr"]`,
//     `[data-no-translate]`, `.notranslate`, `[data-payslip]` (fiche de paie
//     imprimée), les champs saisis (input, textarea, contenteditable), le code.
//
// Les phrases construites par interpolation (`Bonjour ${nom}`) ne sont PAS dans
// le dictionnaire telles quelles : elles restent à passer par tx().
// ════════════════════════════════════════════════════════════════════════

const ATTRIBUTS = ['placeholder', 'title', 'aria-label', 'alt'];
const BALISES_IGNOREES = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE', 'INPUT', 'SELECT', 'OPTION', 'SVG', 'CANVAS', 'IFRAME']);
const ZONES_FR = '[lang="fr"], [data-no-translate], .notranslate, [data-payslip], [contenteditable=""], [contenteditable="true"]';

/** Clé de dictionnaire d'un texte brut : espaces (insécables compris) repliés. */
export function cleDe(brut) {
  return String(brut).replace(/[\s\u00a0]+/g, ' ').trim();
}

/**
 * Traduction d'un texte brut, ou null. `chercher(cle)` rend la traduction NL d'une
 * clé FR (ou null). Les espaces de tête et de queue du texte d'origine sont gardés :
 * « Total : » suivi d'une valeur reste « Totaal : ».
 */
export function traduireTexte(brut, chercher) {
  if (typeof brut !== 'string' || brut.length < 2) return null;
  const cle = cleDe(brut);
  if (cle.length < 2 || !/[A-Za-zÀ-ÿ]{2}/.test(cle)) return null;
  let nl = chercher(cle);
  // L'apostrophe typographique et la droite se valent dans une clé.
  if (!nl && cle.includes('’')) nl = chercher(cle.replace(/’/g, "'"));
  if (!nl && cle.includes("'")) nl = chercher(cle.replace(/'/g, '’'));
  // 28/10/2026 : « 10 jours/an » et « 20 jours/an » sont le MÊME texte au nombre près.
  if (!nl) nl = parModele(cle, chercher);
  // Un texte qui n'est QU'UNE date (« dimanche 5 avril », « Octobre 2026 »).
  if (!nl) nl = traduireDateSeule(cle);
  if (!nl || nl === cle) return null;
  const tete = brut.match(/^[\s\u00a0]*/)[0];
  const queue = brut.match(/[\s\u00a0]*$/)[0];
  return tete + nl + queue;
}

// ── Modèles : le même texte, au nombre près ─────────────────────────────
// Un nombre (« 2 917,82 », « 20 », « 0,25 ») est remplacé par `#` et la clé du dictionnaire est
// cherchée sous cette forme (« # jours/an », « # € / chèque »). Les nombres d'origine sont remis
// tels quels, dans l'ordre, à la place des `#` de la traduction : AUCUN chiffre n'est écrit
// dans le dictionnaire (un chiffre légal en dur y serait une faute R1), AUCUN n'est modifié.
// Garde-fou : le même nombre de `#` des deux côtés, sinon rien n'est traduit.
const NOMBRE = /\d+(?:[ \u00a0.,]\d+)*/g;
export function parModele(cle, chercher) {
  const nombres = cle.match(NOMBRE);
  if (!nombres) return null;
  const modele = cle.replace(NOMBRE, '#');
  if (modele === '#' || !/[A-Za-zÀ-ÿ]{2}/.test(modele)) return null;
  const nl = chercher(modele);
  if (!nl) return null;
  if ((nl.match(/#/g) || []).length !== nombres.length) return null;
  let i = 0;
  return nl.replace(/#/g, () => nombres[i++]);
}

// ── Dates seules ────────────────────────────────────────────────────────
// Les dates écrites par toLocaleDateString('fr-BE') ne sont pas des phrases : ce sont des noms de
// jours et de mois autour de nombres. Traduites SEULEMENT si le texte entier n'est que cela.
const MOIS = { janvier: 'januari', février: 'februari', mars: 'maart', avril: 'april', mai: 'mei', juin: 'juni', juillet: 'juli', août: 'augustus', septembre: 'september', octobre: 'oktober', novembre: 'november', décembre: 'december' };
const MOIS_COURTS = { janv: 'jan', jan: 'jan', fév: 'feb', févr: 'feb', mars: 'mrt', avr: 'apr', mai: 'mei', juin: 'jun', juil: 'jul', août: 'aug', sept: 'sep', sep: 'sep', oct: 'okt', nov: 'nov', déc: 'dec', dec: 'dec' };
const JOURS = { lundi: 'maandag', mardi: 'dinsdag', mercredi: 'woensdag', jeudi: 'donderdag', vendredi: 'vrijdag', samedi: 'zaterdag', dimanche: 'zondag' };
const JOURS_COURTS = { lun: 'ma', mar: 'di', mer: 'wo', jeu: 'do', ven: 'vr', sam: 'za', dim: 'zo' };
function casse(modele, mot) { return modele[0] === modele[0].toUpperCase() && modele[0] !== modele[0].toLowerCase() ? mot[0].toUpperCase() + mot.slice(1) : mot; }
export function traduireDateSeule(cle) {
  const mots = cle.split(' ');
  if (mots.length > 5) return null;
  let moisVu = false;
  const sortie = [];
  for (const m of mots) {
    const bas = m.toLowerCase().replace(/\.$/, '');
    if (/^\d{1,2}(er)?$/.test(m)) { sortie.push(m.replace(/er$/, '')); continue; }
    if (/^\d{4}$/.test(m)) { sortie.push(m); continue; }
    if (JOURS[bas]) { sortie.push(casse(m, JOURS[bas])); continue; }
    if (MOIS[bas]) { moisVu = true; sortie.push(casse(m, MOIS[bas])); continue; }
    if (mots.length === 1 && MOIS_COURTS[bas]) { moisVu = true; sortie.push(casse(m, MOIS_COURTS[bas])); continue; }
    if (mots.length === 1 && JOURS_COURTS[bas]) { sortie.push(casse(m, JOURS_COURTS[bas])); continue; }
    return null; // un seul mot étranger à une date : ce n'est pas une date, on ne devine pas
  }
  // Sans nom de mois (ou de jour court seul), ce n'est pas une date : « 12 2026 » reste tel quel.
  if (!moisVu && !(mots.length === 1 && JOURS_COURTS[mots[0].toLowerCase().replace(/\.$/, '')])) return null;
  const nl = sortie.join(' ');
  return nl === cle ? null : nl;
}

/** `chercher` pour un dictionnaire { fr: { nl } } et, en repli, une fonction d'overlay. */
export function chercheurDepuis(dico, overlay) {
  return (cle) => {
    const e = dico && Object.prototype.hasOwnProperty.call(dico, cle) ? dico[cle] : null;
    if (e && e.nl) return e.nl;
    return overlay ? overlay(cle) : null;
  };
}

function dansZoneFrancaise(el) {
  return !!(el && el.closest && el.closest(ZONES_FR));
}

/**
 * Branche le traducteur sur `doc` et rend `arreter()` : débranche ET restitue les
 * textes d'origine. Ne lève jamais.
 */
export function demarrerTraducteurNl(doc, chercher) {
  const win = doc && doc.defaultView;
  if (!doc || !win || !doc.body || typeof chercher !== 'function') return () => {};

  // Nœud de texte → { fr, nl } : `fr` d'origine, `nl` ce que NOUS avons écrit.
  const textes = new WeakMap();
  const suivis = new Set();     // nœuds de texte touchés (pour restaurer)
  // Élément → { attribut: { fr, nl } }
  const attrs = new WeakMap();
  const elemsSuivis = new Set();

  const traiterTexte = (noeud) => {
    const parent = noeud.parentElement;
    if (!parent || BALISES_IGNOREES.has(parent.tagName.toUpperCase()) || dansZoneFrancaise(parent)) return;
    const memo = textes.get(noeud);
    // Texte que nous avons posé nous-mêmes : rien à faire. Sinon React (ou autre) l'a réécrit : on repart de zéro.
    if (memo && noeud.nodeValue === memo.nl) return;
    const neuf = traduireTexte(noeud.nodeValue, chercher);
    if (neuf === null) { if (memo) textes.delete(noeud); return; }
    textes.set(noeud, { fr: noeud.nodeValue, nl: neuf });
    suivis.add(noeud);
    noeud.nodeValue = neuf;
  };

  const traiterAttributs = (el) => {
    if (BALISES_IGNOREES.has(el.tagName.toUpperCase()) && el.tagName.toUpperCase() !== 'INPUT' && el.tagName.toUpperCase() !== 'TEXTAREA') return;
    if (dansZoneFrancaise(el)) return;
    for (const a of ATTRIBUTS) {
      if (!el.hasAttribute(a)) continue;
      const val = el.getAttribute(a);
      const reg = attrs.get(el) || {};
      if (reg[a] && val === reg[a].nl) continue;
      const neuf = traduireTexte(val, chercher);
      if (neuf === null) { if (reg[a]) { delete reg[a]; } continue; }
      reg[a] = { fr: val, nl: neuf };
      attrs.set(el, reg);
      elemsSuivis.add(el);
      el.setAttribute(a, neuf);
    }
  };

  const parcourir = (racine) => {
    if (!racine) return;
    if (racine.nodeType === 3) { traiterTexte(racine); return; }
    if (racine.nodeType !== 1) return;
    if (BALISES_IGNOREES.has(racine.tagName.toUpperCase()) && !['INPUT', 'TEXTAREA'].includes(racine.tagName.toUpperCase())) return;
    if (dansZoneFrancaise(racine)) return;
    traiterAttributs(racine);
    const marche = doc.createTreeWalker(racine, 1 | 4 /* ELEMENT | TEXT */, null);
    let n = marche.nextNode();
    while (n) {
      if (n.nodeType === 3) traiterTexte(n);
      else if (n.nodeType === 1) traiterAttributs(n);
      n = marche.nextNode();
    }
  };

  const aTraiter = new Set();
  let plan = null;
  const passer = () => {
    plan = null;
    const lot = [...aTraiter];
    aTraiter.clear();
    for (const n of lot) {
      try { if (n.isConnected) parcourir(n); } catch { /* un nœud réfractaire n'arrête pas le passage */ }
    }
  };
  const planifier = () => {
    if (plan !== null) return;
    plan = win.requestAnimationFrame ? win.requestAnimationFrame(passer) : win.setTimeout(passer, 16);
  };

  let observeur = null;
  try {
    observeur = new win.MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList') m.addedNodes.forEach((n) => aTraiter.add(n));
        else if (m.type === 'characterData') aTraiter.add(m.target);
        else if (m.type === 'attributes') aTraiter.add(m.target);
      }
      if (aTraiter.size) planifier();
    });
    observeur.observe(doc.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTS });
  } catch { /* handled */ }

  aTraiter.add(doc.body);
  planifier();

  return function arreter() {
    try { if (observeur) observeur.disconnect(); } catch { /* handled */ }
    if (plan !== null) {
      try { if (win.cancelAnimationFrame) win.cancelAnimationFrame(plan); else win.clearTimeout(plan); } catch { /* handled */ }
      plan = null;
    }
    aTraiter.clear();
    for (const n of suivis) {
      const m = textes.get(n);
      if (m && n.nodeValue === m.nl) n.nodeValue = m.fr;
    }
    suivis.clear();
    for (const el of elemsSuivis) {
      const reg = attrs.get(el) || {};
      for (const a of Object.keys(reg)) if (el.getAttribute(a) === reg[a].nl) el.setAttribute(a, reg[a].fr);
    }
    elemsSuivis.clear();
  };
}
